/**
 * Hook for managing WebSocket connection to a game room.
 *
 * Subscribes to room:subscribe via the WS store and routes
 * game:room_updated messages. Navigates to the game screen
 * when room status changes to IN_PROGRESS.
 */

import { useEffect, useRef, useState } from "react";
import { buildGamePath, navigateTo, type RouteMode } from "../../router";
import type { GameRoom } from "../game-client/types";
import type { useLobbyActions } from "../store/lobby-store";
import { wsActions } from "../store/ws-store";

type ConnectionStatus = "connected" | "reconnecting";

interface UseGameRoomWSResult {
  readonly connectionStatus: ConnectionStatus;
}

export function useGameRoomWS(
  roomId: string | null,
  gameType: string,
  lobbyActions: ReturnType<typeof useLobbyActions>,
  routeMode: RouteMode = "v2",
): UseGameRoomWSResult {
  const [connectionStatus, setConnectionStatus] =
    useState<ConnectionStatus>("reconnecting");

  const lobbyActionsRef = useRef(lobbyActions);
  useEffect(() => {
    lobbyActionsRef.current = lobbyActions;
  }, [lobbyActions]);

  useEffect(() => {
    if (!roomId) return;

    const handleRoomUpdate = (room: GameRoom) => {
      lobbyActionsRef.current.updateCurrentRoom(room);
      if (room.status === "IN_PROGRESS") {
        navigateTo(buildGamePath(gameType, roomId, routeMode));
      }
    };

    const unsubscribeRoom = wsActions.subscribeRoom(roomId, (message) => {
      setConnectionStatus("connected");

      if (message.type === "game:room_updated") {
        handleRoomUpdate(message.room);
      }
    });

    const unsubscribeConnection = wsActions.onConnectionChange((status) => {
      setConnectionStatus(
        status === "connected" ? "connected" : "reconnecting",
      );
    });

    return () => {
      unsubscribeRoom();
      unsubscribeConnection();
    };
  }, [roomId, gameType, routeMode]);

  return { connectionStatus };
}
